import { type VariantProps, cva } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef } from "react";

import { cn } from "@/lib/cn";

import Icon from "../Icon/Icon";
import type { IconName } from "../Icon/iconMap";
import { Loader } from "../Loading/Loader";
import { TooltipWrapper } from "../Tooltip/TooltipWrapper";

type Props = ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<VariantProps<typeof variants>, "state"> & {
    /** 表示するアイコン名 */
    icon: IconName;
    /** ローディング状態 */
    isLoading?: boolean;
    /**
     * ボタンのサイズ
     * Default: 36x36
     * Large: 40x40
     * Small: 32x32
     * ExtraSmall: 20x20
     */
    size?: "Default" | "Large" | "Small" | "ExtraSmall";
    /** アクセシビリティ用ラベル（必須） */
    "aria-label"?: string;
    /** ツールチップの内容 */
    tooltip?: string;
  };

const variants = cva(
  cn(
    "flex items-center justify-center p-0 rounded-md",
    "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
  ),
  {
    variants: {
      variant: {
        primary: cn(
          "bg-primary-fill shadow-xs", // 共通（エフェクト・背景）
          "text-primary-on-fill outline-primary-on-fill", // 共通カラー（テキスト・アウトライン）
          "hover:bg-primary-fill-hovered", // hover
        ),
        secondary: cn(
          "bg-secondary-muted-fill shadow-xs", // 共通（エフェクト・背景）
          "text-secondary-on-muted-fill outline-secondary-on-muted-fill", // 共通カラー（テキスト）
          "hover:bg-secondary-muted-fill-hovered", // hover
        ),
        neutral: cn(
          "bg-neutral-fill shadow-xs", // 共通（背景・アウトライン）
          "border border-neutral-muted-border text-neutral-on-fill outline-neutral-on-fill", // 共通カラー（テキスト・アウトライン）
          "hover:bg-neutral-fill-hovered", // hover
          "disabled:border-neutral-border-disabled", // disabled
        ),
        ghost: cn(
          "text-neutral-on-fill outline-neutral-on-fill", // 共通カラー（テキスト・アウトライン）
          "hover:bg-neutral-fill-hovered", // hover
        ),
        outline: cn(
          "border border-neutral-border", // 共通（背景・アウトライン）
          "text-neutral-on-fill outline-neutral-on-fill", // 共通カラー（テキスト・アウトライン）
          "hover:bg-neutral-fill-hovered", // hover
          "disabled:border-neutral-border-disabled", // disabled
        ),
        error: cn(
          "text-error-on-neutral-fill outline-error-on-neutral-fill", // 共通（テキスト・アウトライン）
          "hover:bg-error-muted-fill hover:text-error-on-muted-fill", // hover
        ),
        mutedGhost: cn(
          "text-neutral-muted-on-fill outline-neutral-muted-on-fill", // 共通（テキスト・アウトライン）
          "hover:bg-neutral-fill-hovered", // hover
        ),
      },
      size: {
        Default: "w-9 h-9",
        Large: "w-10 h-10",
        Small: "w-8 h-8",
        ExtraSmall: "w-5 h-5",
      },
      state: {
        disabled: cn(
          "cursor-not-allowed",
          "bg-neutral-fill-disabled text-neutral-on-fill-disabled outline-neutral-on-fill-disabled",
        ),
        loading: "opacity-50 cursor-wait",
      },
    },
    defaultVariants: {
      variant: "ghost",
      size: "Default",
    },
  },
);

const IconButton = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    icon,
    variant = "ghost",
    isLoading = false,
    disabled = false,
    className,
    size = "Default",
    "aria-label": ariaLabel,
    tooltip,
    type = "button",
    ...rest
  } = props;

  const state = isLoading ? "loading" : disabled ? "disabled" : undefined;

  const Button = (
    <button
      ref={ref}
      className={cn(variants({ variant, size, state }), className)}
      disabled={disabled}
      aria-label={ariaLabel}
      type={type}
      {...rest}
    >
      {isLoading ? (
        <Loader size="size-4" variant="current" />
      ) : (
        <Icon iconName={icon} size={16} inheritColor />
      )}
    </button>
  );

  // disabled時はdivラップ＋cursor-not-allowed
  if (disabled || isLoading) {
    return (
      <TooltipWrapper
        content={tooltip}
        containerClassName="pointer-events-none"
      >
        <span>{Button}</span>
      </TooltipWrapper>
    );
  }

  return <TooltipWrapper content={tooltip}>{Button}</TooltipWrapper>;
});

IconButton.displayName = "IconButton";

export default IconButton;
